import type { Collection, Table } from 'dexie';
import { STORAGE_KEY_TENANT_ID } from './constants';
import type { Tenanted } from './schema';

function readStorage(): string | null {
  if (typeof globalThis.localStorage === 'undefined') return null;
  return globalThis.localStorage.getItem(STORAGE_KEY_TENANT_ID);
}

function writeStorage(id: string): void {
  if (typeof globalThis.localStorage === 'undefined') return;
  globalThis.localStorage.setItem(STORAGE_KEY_TENANT_ID, id);
}

// Source de vérité du tenantId courant. Lecture localStorage, fallback
// `crypto.randomUUID()` + persist au premier appel — décision Hamid n°19.
export function getCurrentTenantId(): string {
  const existing = readStorage();
  if (existing && existing.length > 0) return existing;
  const generated = crypto.randomUUID();
  writeStorage(generated);
  return generated;
}

// Setter explicite — utilisé en boot d'une nouvelle installation pour
// importer un tenantId connu, et par les tests pour forcer un fixture.
export function setCurrentTenantId(id: string): void {
  if (!id) throw new Error('tenantId required');
  writeStorage(id);
}

// Wrapper systématique des requêtes Dexie : garantit que toute lecture/écriture
// passe par un filtre `tenantId` non contournable. Toute requête qui n'utilise
// pas `withTenant()` est suspecte (cf. revue Claude).
export interface TenantScope<T extends Tenanted, Key> {
  readonly tenantId: string;
  readonly table: Table<T, Key>;
  collection(): Collection<T, Key>;
  toArray(): Promise<T[]>;
  count(): Promise<number>;
  get(id: Key): Promise<T | undefined>;
  add(value: Omit<T, 'tenantId'>): Promise<Key>;
  update(id: Key, changes: Partial<Omit<T, 'tenantId' | 'id'>>): Promise<number>;
  delete(id: Key): Promise<number>;
}

export function withTenant<T extends Tenanted, Key>(
  table: Table<T, Key>,
  tenantId: string = getCurrentTenantId(),
): TenantScope<T, Key> {
  return {
    tenantId,
    table,
    collection: () => table.where('tenantId').equals(tenantId),
    toArray: () => table.where('tenantId').equals(tenantId).toArray(),
    count: () => table.where('tenantId').equals(tenantId).count(),
    get: async (id) => {
      const rec = await table.get(id);
      return rec && rec.tenantId === tenantId ? rec : undefined;
    },
    add: async (value) => {
      // Cast simple `as T` justifié : `value` est typé `Omit<T, 'tenantId'>`,
      // donc `{ ...value, tenantId }` reconstitue bien `T`. Le compilateur ne sait
      // pas inférer cette équivalence sur les génériques contraints par marker.
      const enriched = { ...value, tenantId } as T;
      return table.add(enriched);
    },
    update: async (id, changes) => {
      const rec = await table.get(id);
      if (!rec || rec.tenantId !== tenantId) return 0;
      // tenantId est interdit dans changes (typé Omit) — pas de risque d'écraser.
      return table.update(id, changes as object);
    },
    delete: async (id) => {
      const rec = await table.get(id);
      if (!rec || rec.tenantId !== tenantId) return 0;
      await table.delete(id);
      return 1;
    },
  };
}
